import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export const Contact = () => {
  const {
    t
  } = useLanguage();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitted(true);
      setFormData({
        name: '',
        phone: '',
        message: ''
      });
    }, 500);
  };
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div className="w-full bg-white pt-32 pb-20" data-id="element-197">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-198">
        {/* Header */}
        <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="text-center mb-16" data-id="element-199">
          <h1 className="text-4xl md:text-5xl font-bold text-navy mb-6" data-id="element-200">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-201">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-202">
          {/* Contact Info & Map */}
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="space-y-10" data-id="element-203">
            <div data-id="element-204">
              <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-205">
                {t('contact.info')}
              </h3>
              <div className="space-y-6" data-id="element-206">
                <div className="flex items-start gap-4" data-id="element-207">
                  <div className="w-12 h-12 bg-pink-lighter rounded-full flex items-center justify-center text-pink-primary shrink-0" data-id="element-208">
                    <MapPin className="w-6 h-6" data-id="element-209" />
                  </div>
                  <div data-id="element-210">
                    <h4 className="font-semibold text-navy" data-id="element-211">Location</h4>
                    <p className="text-gray-600" data-id="element-212">{t('contact.address')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-213">
                  <div className="w-12 h-12 bg-pink-lighter rounded-full flex items-center justify-center text-pink-primary shrink-0" data-id="element-214">
                    <Phone className="w-6 h-6" data-id="element-215" />
                  </div>
                  <div data-id="element-216">
                    <h4 className="font-semibold text-navy" data-id="element-217">Phone</h4>
                    <p className="text-gray-600" data-id="element-218">{t('contact.phone')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-219">
                  <div className="w-12 h-12 bg-pink-lighter rounded-full flex items-center justify-center text-pink-primary shrink-0" data-id="element-220">
                    <Mail className="w-6 h-6" data-id="element-221" />
                  </div>
                  <div data-id="element-222">
                    <h4 className="font-semibold text-navy" data-id="element-223">Email</h4>
                    <p className="text-gray-600" data-id="element-224">{t('contact.email')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-225">
                  <div className="w-12 h-12 bg-pink-lighter rounded-full flex items-center justify-center text-pink-primary shrink-0" data-id="element-226">
                    <Clock className="w-6 h-6" data-id="element-227" />
                  </div>
                  <div data-id="element-228">
                    <h4 className="font-semibold text-navy" data-id="element-229">Business Hours</h4>
                    <p className="text-gray-600" data-id="element-230">{t('contact.hours')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="h-64 w-full rounded-3xl overflow-hidden shadow-md border border-gray-100" data-id="element-231">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d94863.38318288591!2d-70.78160351110055!3d42.09670081691119!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e4a30b1b11b51b%3A0x868b311c6d17b0!2sMarshfield%2C%20MA!5e0!3m2!1sen!2sus!4v1715000000000!5m2!1sen!2sus" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Marshfield MA Map" data-id="element-232"></iframe>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="bg-white p-8 md:p-10 rounded-[2rem] shadow-xl border border-pink-50 h-fit" data-id="element-233">
            {isSubmitted ? <div className="text-center py-16 space-y-6" data-id="element-234">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-500 mx-auto" data-id="element-235">
                  <CheckCircle2 className="w-10 h-10" data-id="element-236" />
                </div>
                <h3 className="text-2xl font-bold text-navy" data-id="element-237">Message Sent!</h3>
                <p className="text-gray-600" data-id="element-238">{t('contact.form.success')}</p>
                <button onClick={() => setIsSubmitted(false)} className="mt-4 text-pink-primary font-medium hover:underline" data-id="element-239">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-240">
                <div data-id="element-241">
                  <label htmlFor="name" className="block text-sm font-medium text-navy mb-2" data-id="element-242">
                    {t('contact.form.name')}
                  </label>
                  <input type="text" id="name" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all" data-id="element-243" />
                </div>
                <div data-id="element-244">
                  <label htmlFor="phone" className="block text-sm font-medium text-navy mb-2" data-id="element-245">
                    {t('contact.form.phone')}
                  </label>
                  <input type="tel" id="phone" required value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all" data-id="element-246" />
                </div>
                <div data-id="element-247">
                  <label htmlFor="message" className="block text-sm font-medium text-navy mb-2" data-id="element-248">
                    {t('contact.form.message')}
                  </label>
                  <textarea id="message" required rows={5} value={formData.message} onChange={e => setFormData({
                ...formData,
                message: e.target.value
              })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-primary focus:ring-2 focus:ring-pink-primary/20 outline-none transition-all resize-none" data-id="element-249"></textarea>
                </div>
                <button type="submit" className="w-full bg-pink-primary text-white py-4 rounded-xl font-semibold hover:bg-pink-hover transition-colors shadow-md flex items-center justify-center gap-2" data-id="element-250">
                  <Send className="w-5 h-5" data-id="element-251" />
                  {t('contact.form.submit')}
                </button>
              </form>}
          </motion.div>
        </div>
      </div>
    </div>;
};